<script setup>
import { ref, onBeforeUnmount, nextTick } from 'vue'
import Sortable from 'sortablejs'

// Consistent color palette for log types (matches InteractiveHistogram)
const LOG_TYPE_COLORS = {
  conn: '#7357D2',      // Purple (primary)
  dns: '#4A90D9',       // Blue
  http: '#50C878',      // Green
  ssl: '#F5A623',       // Orange
  x509: '#D94A4A',      // Red
  files: '#9B59B6',     // Violet
  smtp: '#1ABC9C',      // Teal
  ssh: '#E74C3C',       // Coral
  dhcp: '#3498DB',      // Light blue
  ntp: '#95A5A6',       // Gray
  rdp: '#E67E22',       // Dark orange
  ftp: '#27AE60',       // Dark green
  sip: '#8E44AD',       // Dark purple
  snmp: '#2980B9',      // Dark blue
  kerberos: '#C0392B',  // Dark red
  smb: '#16A085',       // Dark teal
  dce_rpc: '#D35400',   // Rust
  ntlm: '#7F8C8D',      // Dark gray
  pe: '#BDC3C7',        // Light gray
  weird: '#E91E63',     // Pink
  notice: '#FF5722',    // Deep orange
  software: '#607D8B',  // Blue gray
  known: '#795548',     // Brown
  _default: '#6B7280'   // Fallback gray
}

// Get color for a log type
function getLogTypeColor(logType) {
  return LOG_TYPE_COLORS[logType] || LOG_TYPE_COLORS._default
}

const props = defineProps({
  logTypeDistribution: {
    type: Array,
    default: () => []
  },
  activeLogType: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  fieldMappings: {
    type: Object,
    default: () => ({})
  },
  isFieldEnabled: {
    type: Function,
    default: () => () => true
  },
  onToggleField: {
    type: Function,
    default: () => () => {}
  },
  getFieldOrder: {
    type: Function,
    default: () => () => []
  },
  onReorderFields: {
    type: Function,
    default: () => () => {}
  },
  onResetLogType: {
    type: Function,
    default: () => () => {}
  },
  hasCustomSettings: {
    type: Function,
    default: () => () => false
  }
})

const emit = defineEmits(['select'])

// Track which log types have expanded field lists
const expandedLogTypes = ref({})

// Local cached field lists for each log type (prevents re-render during drag)
const cachedFieldLists = ref({})

// Store Sortable instances for cleanup
const sortableInstances = ref({})

// Get fields for a log type in order (from API mappings with custom order applied)
function getFieldsForLogType(logType) {
  const apiFields = props.fieldMappings?.[logType] || []
  if (apiFields.length === 0) return []
  return props.getFieldOrder(logType, apiFields)
}

// Get cached fields for rendering (stable reference for Vue)
function getCachedFields(logType) {
  return cachedFieldLists.value[logType] || []
}

// Initialize cached fields and Sortable
function initFieldList(logType) {
  // Cache the current field order
  cachedFieldLists.value[logType] = getFieldsForLogType(logType)

  nextTick(() => {
    const el = document.querySelector(`[data-fields-list="${logType}"]`)
    if (!el) return

    // Destroy existing instance if any
    if (sortableInstances.value[logType]) {
      sortableInstances.value[logType].destroy()
    }

    sortableInstances.value[logType] = Sortable.create(el, {
      animation: 150,
      handle: '.drag-handle',
      ghostClass: 'field-ghost',
      dragClass: 'field-drag',
      forceFallback: true,
      onEnd: (evt) => {
        const { oldIndex, newIndex } = evt
        if (oldIndex === newIndex) return

        // Reorder the cached list
        const newOrder = [...cachedFieldLists.value[logType]]
        const [moved] = newOrder.splice(oldIndex, 1)
        newOrder.splice(newIndex, 0, moved)

        // Update cache and persist
        cachedFieldLists.value[logType] = newOrder
        props.onReorderFields(logType, newOrder)
      }
    })
  })
}

// Cleanup Sortable instance and cache
function cleanupFieldList(logType) {
  if (sortableInstances.value[logType]) {
    sortableInstances.value[logType].destroy()
    delete sortableInstances.value[logType]
  }
  delete cachedFieldLists.value[logType]
}

// Cleanup all on unmount
onBeforeUnmount(() => {
  Object.keys(sortableInstances.value).forEach(cleanupFieldList)
})

// Check if log type has fields
function hasFieldsForLogType(logType) {
  const apiFields = props.fieldMappings?.[logType] || []
  return apiFields.length > 0
}

// Select a log type
function selectLogType(logType) {
  emit('select', logType)
}

// Toggle field list expansion
function toggleExpanded(logType) {
  const wasExpanded = expandedLogTypes.value[logType]
  expandedLogTypes.value[logType] = !wasExpanded

  if (!wasExpanded) {
    // Expanding - initialize field list and Sortable
    initFieldList(logType)
  } else {
    // Collapsing - cleanup
    cleanupFieldList(logType)
  }
}

// Check if log type is active
function isActive(logType) {
  return props.activeLogType === logType
}

// Check if fields are expanded
function isExpanded(logType) {
  return expandedLogTypes.value[logType] || false
}

// Handle field toggle
function handleFieldToggle(logType, field) {
  const allFields = getFieldsForLogType(logType)
  const currentlyEnabled = allFields.filter(f => props.isFieldEnabled(logType, f))

  // If this is the last enabled field and we're trying to disable it, don't allow
  if (currentlyEnabled.length === 1 && currentlyEnabled[0] === field) {
    return
  }

  props.onToggleField(logType, field)
}

// Handle reset for a log type
function handleReset(logType) {
  props.onResetLogType(logType)
  // Refresh the cached field list with default order
  cachedFieldLists.value[logType] = getFieldsForLogType(logType)
}
</script>

<template>
  <div class="log-types-sidebar">
    <div class="sidebar-header">
      <span class="header-title">Log Types</span>
    </div>

    <div class="sidebar-content">
      <!-- Empty state -->
      <div v-if="logTypeDistribution.length === 0 && !loading" class="empty-state">
        <span>No log types</span>
      </div>

      <!-- Loading state -->
      <div v-if="loading && logTypeDistribution.length === 0" class="loading-state">
        <div class="spinner-small"></div>
      </div>

      <!-- Log type list -->
      <div class="log-type-list">
        <div
          v-for="dist in logTypeDistribution"
          :key="dist.log_type"
          class="log-type-item"
        >
          <!-- Log type row -->
          <div
            class="log-type-row"
            :class="{ 'active': isActive(dist.log_type) }"
            @click="selectLogType(dist.log_type)"
          >
            <div class="log-type-info">
              <span
                class="log-type-color"
                :style="{ backgroundColor: getLogTypeColor(dist.log_type) }"
              ></span>
              <span class="log-type-name">{{ dist.log_type }}</span>
              <span class="log-type-count">{{ dist.count.toLocaleString() }}</span>
            </div>
            <button
              v-if="hasFieldsForLogType(dist.log_type)"
              class="expand-btn"
              @click.stop="toggleExpanded(dist.log_type)"
              :title="isExpanded(dist.log_type) ? 'Hide fields' : 'Show fields'"
            >
              <font-awesome-icon
                :icon="['fas', isExpanded(dist.log_type) ? 'chevron-up' : 'chevron-down']"
              />
            </button>
          </div>

          <!-- Fields list (expandable) -->
          <div
            v-if="hasFieldsForLogType(dist.log_type) && isExpanded(dist.log_type)"
            class="fields-panel"
          >
            <div
              class="fields-list"
              :data-fields-list="dist.log_type"
            >
              <div
                v-for="field in getCachedFields(dist.log_type)"
                :key="field"
                class="field-item"
              >
                <input
                  type="checkbox"
                  :checked="isFieldEnabled(dist.log_type, field)"
                  @change="handleFieldToggle(dist.log_type, field)"
                  class="field-checkbox"
                  @click.stop
                />
                <span class="field-name">{{ field }}</span>
                <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
              </div>
            </div>
            <button
              v-if="hasCustomSettings(dist.log_type)"
              class="reset-btn"
              @click.stop="handleReset(dist.log_type)"
              title="Reset to default order and show all fields"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.log-types-sidebar {
  width: 200px;
  min-width: 200px;
  background: #151a22;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.header-title {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9CA3AF;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

/* Custom scrollbar */
.sidebar-content::-webkit-scrollbar {
  width: 6px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: #3E4A56;
  border-radius: 10px;
}

.sidebar-content::-webkit-scrollbar-thumb:hover {
  background: #4E5A66;
}

.empty-state,
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: #6B7280;
  font-size: 0.875rem;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #374151;
  border-top: 2px solid #7357D2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.log-type-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.log-type-item {
  display: flex;
  flex-direction: column;
}

.log-type-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  border: 1px solid transparent;
}

.log-type-row:hover {
  background: rgba(255, 255, 255, 0.05);
}

.log-type-row.active {
  background: rgba(115, 87, 210, 0.2);
  border-color: rgba(115, 87, 210, 0.4);
}

.log-type-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.log-type-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

.log-type-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #E5E7EB;
  text-transform: uppercase;
}

.log-type-row.active .log-type-name {
  color: #fff;
}

.log-type-count {
  font-size: 0.75rem;
  color: #6B7280;
}

.log-type-row.active .log-type-count {
  color: #9CA3AF;
}

.expand-btn {
  background: transparent;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  font-size: 0.7rem;
}

.expand-btn:hover {
  color: #E5E7EB;
}

.fields-list {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0.75rem 0.5rem 1.25rem;
  gap: 0.125rem;
}

.field-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #6B7280;
  font-family: 'Cutive Mono', monospace;
  padding: 0.2rem 0;
  cursor: default;
  transition: color 0.15s ease, opacity 0.15s ease, background-color 0.15s ease;
  border-radius: 0.25rem;
}

.field-item:hover {
  color: #9CA3AF;
}

/* SortableJS ghost (placeholder) */
.field-ghost {
  opacity: 0.4;
  background: rgba(115, 87, 210, 0.1);
}

/* SortableJS drag (element being dragged) */
.field-drag {
  opacity: 0.9;
  background: rgba(115, 87, 210, 0.2);
  border-radius: 0.25rem;
}

.field-checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  border: 1px solid #4B5563;
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.field-checkbox:hover {
  border-color: #7357D2;
}

.field-checkbox:checked {
  background: #7357D2;
  border-color: #7357D2;
}

.field-checkbox:checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.field-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drag-handle {
  color: #4B5563;
  cursor: grab;
  font-size: 0.7rem;
  letter-spacing: -1px;
  padding: 0.25rem;
  opacity: 0.4;
  transition: opacity 0.15s ease, color 0.15s ease;
  user-select: none;
  flex-shrink: 0;
}

.field-item:hover .drag-handle {
  opacity: 1;
  color: #9CA3AF;
}

.drag-handle:hover {
  color: #E5E7EB !important;
}

.drag-handle:active {
  cursor: grabbing;
}

.fields-panel {
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(255, 255, 255, 0.03);
}

.reset-btn {
  align-self: flex-start;
  margin: 0.25rem 0 0.5rem 1.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.65rem;
  color: #6B7280;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.reset-btn:hover {
  color: #E5E7EB;
  border-color: rgba(115, 87, 210, 0.4);
  background: rgba(115, 87, 210, 0.1);
}
</style>
